import { useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";

import useLanguage from "../../hooks/useLanguage";
import useContent from "../../hooks/useContent";
import useToaster from "../../hooks/useToaster";
import CommonPage from "../../components/common-page";

import FilterBar from "./components/FilterBar";
import KPICards from "./components/KPICards";
import MonthChart from "./components/MonthChart";
import SourceChart from "./components/SourceChart";
import UserTable from "./components/UserTable";
import ArticleTable from "./components/ArticleTable";
import DownloadsDialog from "./components/Downloads.dialog";
import { ChartsRow, LoaderWrap } from "./style";

const EMPTY_STATS = {
  totals: { total: 0, downloaded: 0, published: 0, downloadedNotPublished: 0 },
  bySource: [],
  byMonth: [],
  byUser: [],
  byArticle: [],
};

const toYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const currentMonthRange = () => {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  return { from: toYmd(first), to: toYmd(now) };
};

export default function Statistics() {
  const [
    {
      pages: { statistics: text },
    },
  ] = useLanguage();
  const { listUsers, getStatistics } = useContent();
  const { addToaster } = useToaster();

  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [historyTarget, setHistoryTarget] = useState(null);

  // Default range = current calendar month (first → today)
  const defaultRange = currentMonthRange();
  // Filter state (applied)
  const [filters, setFilters] = useState({
    from: defaultRange.from,
    to: defaultRange.to,
    userId: "",
    sources: [],
    excludeAdmins: false,
  });
  // Filter state (draft, edited by inputs)
  const [draft, setDraft] = useState({
    from: defaultRange.from,
    to: defaultRange.to,
    userId: "",
    sources: [],
    excludeAdmins: false,
  });

  const fetchUsers = async () => {
    try {
      const data = await listUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch {
      setUsers([]);
    }
  };

  const fetchStats = async (f) => {
    setLoading(true);
    try {
      const data = await getStatistics({
        from: f.from || undefined,
        to: f.to || undefined,
        userId: f.userId || undefined,
        source: f.sources.length ? f.sources.join(",") : undefined,
        excludeAdmins: f.excludeAdmins ? "true" : undefined,
      });
      setStats(data || EMPTY_STATS);
    } catch {
      addToaster({ message: text.toaster.loadFailed, severity: "error" });
      setStats(EMPTY_STATS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStats(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleApply = () => {
    setFilters(draft);
    fetchStats(draft);
  };

  const handleClear = () => {
    const reset = currentMonthRange();
    const next = {
      from: reset.from,
      to: reset.to,
      userId: "",
      sources: [],
      excludeAdmins: false,
    };
    setDraft(next);
    setFilters(next);
    fetchStats(next);
  };

  return (
    <CommonPage title={text.title} subtitle={text.subtitle} maxWidth={false}>
      <FilterBar
        text={text}
        users={users}
        from={draft.from}
        to={draft.to}
        userId={draft.userId}
        sources={draft.sources}
        excludeAdmins={draft.excludeAdmins}
        onFromChange={(v) => setDraft((d) => ({ ...d, from: v }))}
        onToChange={(v) => setDraft((d) => ({ ...d, to: v }))}
        onUserChange={(v) => setDraft((d) => ({ ...d, userId: v }))}
        onSourcesChange={(v) => setDraft((d) => ({ ...d, sources: v }))}
        onExcludeAdminsChange={(v) =>
          setDraft((d) => ({ ...d, excludeAdmins: v }))
        }
        onApply={handleApply}
        onClear={handleClear}
      />

      {loading ? (
        <LoaderWrap>
          <CircularProgress />
        </LoaderWrap>
      ) : (
        <Box>
          <KPICards text={text} totals={stats.totals} />
          <ChartsRow>
            <MonthChart text={text} data={stats.byMonth} />
            <SourceChart text={text} data={stats.bySource} />
          </ChartsRow>
          <UserTable text={text} data={stats.byUser} />
          <ArticleTable
            text={text}
            data={stats.byArticle || []}
            onShowHistory={(item) =>
              setHistoryTarget({ id: item.id, title: item.title })
            }
          />
        </Box>
      )}
      <DownloadsDialog
        open={!!historyTarget}
        target={historyTarget}
        onClose={() => setHistoryTarget(null)}
      />
    </CommonPage>
  );
}
